import React from 'react';
import { motion } from 'motion/react';
import { Trophy, X } from 'lucide-react';

interface RankingPrize {
  id: string;
  text?: string;
  imageUrl?: string;
  description?: string;
  groupKey: string;
  position: number;
}

interface PrizeDetailModalProps {
  prize: RankingPrize;
  onClose: () => void;
}

export function PrizeDetailModal({ prize, onClose }: PrizeDetailModalProps) {
  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      {/* Backdrop tap to close */}
      <div className="absolute inset-0" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        className="relative w-full max-w-md bg-gradient-to-b from-[#1e293b]/95 to-[#0f172a]/98 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col z-10"
      >
        {/* Subtle top light reflection */}
        <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 active:scale-95 rounded-full transition-all text-white/70 hover:text-white z-20 border border-white/5"
        >
          <X size={16} />
        </button>

        {/* Header Icon + Label */}
        <div className="p-8 pb-4 flex flex-col items-center text-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-[#FACC15] bg-[#FACC15]/10 px-4 py-1.5 rounded-full border border-[#FACC15]/20 shadow-md">
            Premio Posición Número {prize.position}
          </span>
        </div>

        {/* Visual Showcase (Simple premium standard image display) */}
        <div className="px-8 pb-6 flex flex-col items-center">
          <div className="relative w-full aspect-square max-w-[260px] rounded-[2rem] flex items-center justify-center bg-black/40 border border-white/10 shadow-lg group transition-all duration-500 hover:scale-[1.03] hover:border-white/30 hover:shadow-[0_0_25px_rgba(255,255,255,0.25)]">
            {prize.imageUrl ? (
              <img
                src={prize.imageUrl}
                alt={prize.text}
                className="w-full h-full object-cover rounded-[2rem] transition-transform duration-500 ease-out group-hover:scale-[1.10]"
              />
            ) : (
              <Trophy size={96} className="opacity-10 text-white" />
            )}
          </div>
        </div>

        {/* Text details */}
        <div className="px-8 pb-8 text-center flex flex-col items-center">
          <p className="text-xl font-black uppercase tracking-tight text-white leading-snug break-words max-w-xs drop-shadow-md">
            {prize.text || 'Sin nombre'}
          </p>

          {prize.description && (
            <p className="mt-3 text-xs font-bold text-white/70 leading-relaxed break-words text-center w-full bg-white/5 border border-white/5 px-4 py-3 rounded-2xl">
              {prize.description}
            </p>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full max-w-[200px] bg-white/5 hover:bg-white/10 active:scale-98 border border-white/10 text-white py-3 rounded-xl font-black uppercase text-[11px] tracking-wider transition-all shadow-md hover:shadow-lg"
          >
            Entendido
          </button>
        </div>
      </motion.div>
    </div>
  );
}
